import clsx from "clsx";
import { useEffect, useState } from "react";
import { Control, useFormState } from "react-hook-form";

import { useFeatureflags, useOrderFormConfig } from "@web-app/api";
import {
  Button,
  Header,
  Icon,
  Label,
  SelectInput,
  Skeleton,
  Tabs,
} from "@web-app/components";
import type {
  CustomField,
  CustomFieldValue,
  SavedAddress,
} from "@web-app/types";
import {
  GoogleAddress,
  OrderDeliveryType,
  OrderPriorityType,
} from "@web-app/types";

import {
  extractCustomFieldId,
  isCustomField,
} from "../../../settings/FormBuilderTab/constants";
import type {
  DetailsType,
  OrderFormField,
} from "../../../settings/FormBuilderTab/types";
import { ORDER_TYPES } from "../constants";
import { isSectionVisible } from "../sectionVisibility";
import { FormValues } from "../types";

import { AdditionalDetailsTab } from "./tabs/AdditionalDetailsTab";
import { ConfigDrivenTab } from "./tabs/ConfigDrivenTab";
import { DropoffDetailsTab } from "./tabs/DropoffDetailsTab";
import { OrderDetailsTab } from "./tabs/OrderDetailsTab";
import { PickupDetailsTab } from "./tabs/PickupDetailsTab";

function getFormFieldNames(
  fieldKey: string,
  detailsType: DetailsType,
): (keyof FormValues)[] {
  const isPickup = detailsType === "pickup";
  switch (fieldKey) {
    case "systemField_fullName":
      return [isPickup ? "pickupName" : "dropOffName"];
    case "systemField_phone":
      return [isPickup ? "pickupPhone" : "dropOffPhone"];
    case "systemField_address":
      return [isPickup ? "pickupLocation" : "dropoffLocation"];
    case "systemField_pickupAt":
      return ["scheduledStartDateTime", "pickupEndTime"];
    case "systemField_dropoffAt":
      return ["dropOffStartTime", "scheduledEndDateTime"];
    case "systemField_businessName":
      return [isPickup ? "pickupBusinessName" : "dropOffBusinessName"];
    case "systemField_serviceTime":
      return [isPickup ? "pickupServiceTime" : "dropOffServiceTime"];
    case "systemField_description":
      return ["description"];
    case "systemField_vehicleType":
      return ["vehicleType"];
    case "systemField_value":
      return ["value"];
    case "systemField_tip":
      return ["tip"];
    case "systemField_itemsCount":
      return ["itemsCount"];
    case "systemField_priority":
      return ["priority"];
    case "systemField_externalId":
      return ["externalId"];
    case "systemField_timezone":
      return ["timezone"];
    case "systemField_note":
      return [isPickup ? "pickupNote" : "dropOffNote"];
    case "systemField_saveDetails":
      return [isPickup ? "savePickupDetails" : "saveDropoffDetails"];
    case "systemField_orderRequirements":
      return [
        `${isPickup ? "pickupOrderRequirements" : "dropoffOrderRequirements"}_SIGNATURE` as keyof FormValues,
        `${isPickup ? "pickupOrderRequirements" : "dropoffOrderRequirements"}_PHOTO` as keyof FormValues,
        `${isPickup ? "pickupOrderRequirements" : "dropoffOrderRequirements"}_NOTES` as keyof FormValues,
        `${isPickup ? "pickupOrderRequirements" : "dropoffOrderRequirements"}_ITEMCHECKLIST` as keyof FormValues,
      ];
    case "systemField_length":
      return ["length"];
    case "systemField_width":
      return ["width"];
    case "systemField_height":
      return ["height"];
    case "systemField_weight":
      return ["weight"];
    case "systemField_disableSMS":
      return ["disableSMS"];
    case "systemField_itemChecklist":
      return [isPickup ? "pickupChecklist" : "dropoffChecklist"];
    case "systemField_connectedWorkspaceId":
      return ["connectedWorkspaceId"];
    default:
      return [];
  }
}

type TabItem = {
  content: React.ReactNode;
  id: string;
  title: React.ReactNode;
};

type FormProps = {
  className?: string;
  control: Control<FormValues>;
  currentWorkspaceId: string;
  customFieldErrors?: Record<string, string>;
  customFieldsState: Array<{
    definition: CustomField;
    values: CustomFieldValue[];
  }>;
  deliveryType: OrderDeliveryType;
  dropoffLocation: GoogleAddress | null;
  handleAddressSelect: (address: SavedAddress, isPickup: boolean) => void;
  isTabComplete: (tabName: string) => boolean;
  onAddressSelect: (address: GoogleAddress | null, isPickup: boolean) => void;
  onClose: () => void;
  onDeliveryTypeChange: (value: OrderDeliveryType) => void;
  onPriorityChange: (value: OrderPriorityType) => void;
  onSaveDropoffDetailsChange: (value: boolean) => void;
  onSavePickupDetailsChange: (value: boolean) => void;
  pickupLocation: GoogleAddress | null;
  priority: OrderPriorityType;
  saveDropoffDetails: boolean;
  savePickupDetails: boolean;
  setCustomFields: React.Dispatch<
    React.SetStateAction<
      Array<{ definition: CustomField; values: CustomFieldValue[] }>
    >
  >;
  setWorkspaceOptionType: (value: string) => void;
  workspaceOptionType: string;
};

export default function Form({
  deliveryType,
  pickupLocation,
  dropoffLocation,
  priority,
  savePickupDetails,
  saveDropoffDetails,
  onDeliveryTypeChange,
  onAddressSelect,
  onPriorityChange,
  onSavePickupDetailsChange,
  onSaveDropoffDetailsChange,
  onClose,
  currentWorkspaceId,
  setCustomFields,
  control,
  isTabComplete,
  handleAddressSelect,
  className,
  customFieldsState,
  customFieldErrors,
  workspaceOptionType,
  setWorkspaceOptionType,
}: FormProps) {
  const { errors } = useFormState({ control });
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const isIndividualDelivery =
    deliveryType === OrderDeliveryType.INDIVIDUAL_PICKUP_AND_DROPOFF;

  const { hasFeature, isLoading: isFeatureflagsLoading } = useFeatureflags();
  const isFormBuilderEnabled = hasFeature("formBuilder");
  const { data: formConfig, isLoading: isFormConfigLoading } =
    useOrderFormConfig({ enabled: isFormBuilderEnabled });
  const isConfigLoading =
    isFeatureflagsLoading || (isFormBuilderEnabled && isFormConfigLoading);

  useEffect(() => {
    setSelectedTabIndex(0);
  }, [deliveryType]);

  const configFields: OrderFormField[] = formConfig?.fields ?? [];
  const pickupConfigFields = configFields
    .filter((f) => f.detailsType === "pickup")
    .sort((a, b) => a.position - b.position);
  const dropoffConfigFields = configFields
    .filter((f) => f.detailsType === "dropoff")
    .sort((a, b) => a.position - b.position);
  const orderConfigFields = configFields
    .filter((f) => f.detailsType === "order")
    .sort((a, b) => a.position - b.position);

  const pickupErrorFieldNames = isFormBuilderEnabled
    ? pickupConfigFields.flatMap((f) => getFormFieldNames(f.fieldKey, "pickup"))
    : [
        "pickupName",
        "pickupPhone",
        "pickupLocation",
        "pickupBusinessName",
        "pickupServiceTime",
        "scheduledStartDateTime",
        "pickupEndTime",
      ];

  const dropoffErrorFieldNames = isFormBuilderEnabled
    ? dropoffConfigFields.flatMap((f) =>
        getFormFieldNames(f.fieldKey, "dropoff"),
      )
    : [
        "dropOffName",
        "dropOffPhone",
        "dropoffLocation",
        "dropOffBusinessName",
        "dropOffServiceTime",
        "dropOffStartTime",
        "scheduledEndDateTime",
      ];

  const orderErrorFieldNames = isFormBuilderEnabled
    ? orderConfigFields.flatMap((f) => getFormFieldNames(f.fieldKey, "order"))
    : ["description", "value", "itemsCount", "vehicleType", "priority"];

  const countCustomFieldErrors = (tabFields: OrderFormField[]) =>
    tabFields.filter(
      (f) =>
        isCustomField(f.fieldKey) &&
        !!customFieldErrors?.[extractCustomFieldId(f.fieldKey)],
    ).length;

  const pickupErrors =
    Object.keys(errors).filter((key) =>
      pickupErrorFieldNames.includes(key as keyof FormValues),
    ).length + countCustomFieldErrors(pickupConfigFields);

  const dropoffErrors =
    Object.keys(errors).filter((key) =>
      dropoffErrorFieldNames.includes(key as keyof FormValues),
    ).length + countCustomFieldErrors(dropoffConfigFields);

  const orderDetailsErrors =
    Object.keys(errors).filter((key) =>
      orderErrorFieldNames.includes(key as keyof FormValues),
    ).length + countCustomFieldErrors(orderConfigFields);

  const handleDeliveryTypeChange = (value: OrderDeliveryType) => {
    onDeliveryTypeChange(value);
  };

  const options = ORDER_TYPES.map((option) => ({
    description: option.description,
    label: (
      <div className="flex items-center gap-2">
        {typeof option.icon === "string" && option.icon.startsWith("/") ? (
          <img
            src={option.icon}
            alt={option.name}
            className="w-[18px] h-[22px]"
          />
        ) : (
          <Icon
            name={option.icon as "fasLocationPin" | "fasRoute"}
            className="w-[18px] h-[22px]"
          />
        )}
        <span>{option.name}</span>
      </div>
    ),
    value: option.value.toString(),
  }));

  const configDrivenTabProps = {
    control,
    currentWorkspaceId,
    customFieldErrors,
    customFieldsState,
    deliveryType,
    dropoffLocation,
    handleAddressSelect,
    onAddressSelect,
    onPriorityChange,
    onSaveDropoffDetailsChange,
    onSavePickupDetailsChange,
    pickupLocation,
    priority,
    saveDropoffDetails,
    savePickupDetails,
    setCustomFields,
    setWorkspaceOptionType,
    workspaceOptionType,
  };

  const showPickup = isSectionVisible("pickup", deliveryType);
  const showDropoff = isSectionVisible("dropoff", deliveryType);

  const tabs: TabItem[] =
    isFormBuilderEnabled && formConfig
      ? [
          ...(showPickup
            ? [
                {
                  content: (
                    <ConfigDrivenTab
                      {...configDrivenTabProps}
                      detailsType="pickup"
                      fields={pickupConfigFields}
                    />
                  ),
                  id: "pickup-details",
                  title: (
                    <div className="tw-sidebar-tabs-style">
                      <span>Pickup Details</span>
                      {pickupErrors > 0 ? (
                        <span className="text-red-500">({pickupErrors})</span>
                      ) : (
                        isTabComplete("Pickup Details") && (
                          <Icon name="circleCheck" className="text-green-500" />
                        )
                      )}
                    </div>
                  ),
                },
              ]
            : []),
          ...(showDropoff
            ? [
                {
                  content: (
                    <ConfigDrivenTab
                      {...configDrivenTabProps}
                      detailsType="dropoff"
                      fields={dropoffConfigFields}
                    />
                  ),
                  id: "dropoff-details",
                  title: (
                    <div className="tw-sidebar-tabs-style">
                      <span>Drop-off Details</span>
                      {dropoffErrors > 0 ? (
                        <span className="text-red-500">({dropoffErrors})</span>
                      ) : (
                        isTabComplete("Drop-off Details") && (
                          <Icon name="circleCheck" className="text-green-500" />
                        )
                      )}
                    </div>
                  ),
                },
              ]
            : []),
          {
            content: (
              <ConfigDrivenTab
                {...configDrivenTabProps}
                detailsType="order"
                fields={orderConfigFields}
              />
            ),
            id: "order-details",
            title: (
              <div className="tw-sidebar-tabs-style">
                <span>Order Details</span>
                {orderDetailsErrors > 0 ? (
                  <span className="text-red-500">({orderDetailsErrors})</span>
                ) : (
                  isTabComplete("Order Details") && (
                    <Icon name="circleCheck" className="text-green-500" />
                  )
                )}
              </div>
            ),
          },
        ]
      : [
          ...(showPickup
            ? [
                {
                  content: (
                    <PickupDetailsTab
                      isIndividualDelivery={isIndividualDelivery}
                      pickupLocation={pickupLocation}
                      onAddressSelect={onAddressSelect}
                      control={control}
                      currentWorkspaceId={currentWorkspaceId}
                      savePickupDetails={savePickupDetails}
                      onSavePickupDetailsChange={onSavePickupDetailsChange}
                      handleAddressSelect={handleAddressSelect}
                    />
                  ),
                  id: "pickup-details",
                  title: (
                    <div className="tw-sidebar-tabs-style">
                      <span>Pickup Details</span>
                      {pickupErrors > 0 ? (
                        <span className="text-red-500">({pickupErrors})</span>
                      ) : (
                        isTabComplete("Pickup Details") && (
                          <Icon name="circleCheck" className="text-green-500" />
                        )
                      )}
                    </div>
                  ),
                },
              ]
            : []),
          ...(showDropoff
            ? [
                {
                  content: (
                    <DropoffDetailsTab
                      isIndividualDelivery={isIndividualDelivery}
                      dropoffLocation={dropoffLocation}
                      onAddressSelect={onAddressSelect}
                      control={control}
                      currentWorkspaceId={currentWorkspaceId}
                      saveDropoffDetails={saveDropoffDetails}
                      onSaveDropoffDetailsChange={onSaveDropoffDetailsChange}
                      handleAddressSelect={handleAddressSelect}
                    />
                  ),
                  id: "dropoff-details",
                  title: (
                    <div className="tw-sidebar-tabs-style">
                      <span>Drop-off Details</span>
                      {dropoffErrors > 0 ? (
                        <span className="text-red-500">({dropoffErrors})</span>
                      ) : (
                        isTabComplete("Drop-off Details") && (
                          <Icon name="circleCheck" className="text-green-500" />
                        )
                      )}
                    </div>
                  ),
                },
              ]
            : []),
          {
            content: (
              <OrderDetailsTab
                deliveryType={deliveryType}
                priority={priority}
                onPriorityChange={onPriorityChange}
                control={control}
                workspaceOptionType={workspaceOptionType}
                setWorkspaceOptionType={setWorkspaceOptionType}
              />
            ),
            id: "order-details",
            title: (
              <div className="tw-sidebar-tabs-style">
                <span>Order Details</span>
                {orderDetailsErrors > 0 ? (
                  <span className="text-red-500">({orderDetailsErrors})</span>
                ) : (
                  isTabComplete("Order Details") && (
                    <Icon name="circleCheck" className="text-green-500" />
                  )
                )}
              </div>
            ),
          },
          {
            content: (
              <AdditionalDetailsTab
                currentWorkspaceId={currentWorkspaceId}
                setCustomFields={setCustomFields}
                customFieldsState={customFieldsState}
              />
            ),
            id: "additional-details",
            title: (
              <div className="tw-sidebar-tabs-style">
                <span>Additional Details</span>
                {isTabComplete("Additional Details") && (
                  <Icon name="circleCheck" className="text-green-500" />
                )}
              </div>
            ),
          },
        ];

  return (
    <div
      className={clsx(
        "dispatch-createOrder-drawer",
        "w-[100vw] md:w-[70vw] lg:w-[40vw]",
        className ?? "",
      )}
    >
      <Header
        title="Create Order"
        rightActions={
          <Button
            plain
            onClick={onClose}
            className="text-gray-400 !p-0 hover:bg-white hover:text-gray-500"
          >
            <span className="sr-only">Close</span>
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </Button>
        }
      />

      {isConfigLoading ? (
        <div
          className="px-4 pt-2 pb-4 space-y-3"
          data-testid="form-config-loader"
        >
          <Skeleton height="h-4" width="w-28" />
          <Skeleton height="h-10" width="w-full" />
          <Skeleton height="h-10" width="w-full" />
          <Skeleton height="h-10" width="w-full" />
          <Skeleton height="h-10" width="w-full" />
        </div>
      ) : (
        <>
          <div className="px-4 pt-2 pb-4">
            <Label className="mb-2">Delivery Type</Label>
            <SelectInput
              options={options}
              placeholder="Select delivery type"
              onChange={(value) =>
                handleDeliveryTypeChange(value as OrderDeliveryType)
              }
              selected={deliveryType.toString()}
              required
            />
          </div>

          <Tabs
            tabs={tabs}
            className="px-4"
            {...(isFormBuilderEnabled && formConfig
              ? {
                  onChange: setSelectedTabIndex,
                  selectedIndex: selectedTabIndex,
                }
              : {})}
          />
        </>
      )}
    </div>
  );
}
